import { type Chart, type ChartData, type Plugin } from "chart.js";
import { set } from "lodash";
import moment from "moment";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import { type AnnotationDataset } from "@features/custom-annotations";
import { type PageConfiguration } from "@features/sales-v3/context";
import {
  type ChartConfig,
  type Compare,
} from "@features/sales-v3/hooks/use-chart-state";
import { type TabId } from "@features/sales-v3/tabs";
import {
  COMPARE_METRIC_1_BENCHMARK_ID,
  COMPARE_METRIC_1_ID,
  COMPARE_METRIC_1_LINE_DASH,
  COMPARE_METRIC_2_BENCHMARK_ID,
  COMPARE_METRIC_2_ID,
  COMPARE_METRIC_2_LINE_DASH,
  MAIN_METRIC_BENCHMARK_ID,
  MAIN_METRIC_ID,
  buildCompareDateRangeData,
  buildDataset,
  calculateMetricValue,
  createCompare1BarChartPattern,
  createCompare2BarChartPattern,
  type Y_AXIS_ID,
} from "@features/sales-v3/utils/chart";
import { type ChartDatasets } from "@features/sales-v3/utils/chart-preprocessing";
import { type GetCompareQueryMetadata } from "@features/sales-v3/utils/compare-query";
import { type DesignElementManager } from "@hooks/use-design-element-manager";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import {
  metrics,
  type DateInterval,
  type TimeGranularity,
} from "@north-beam/nb-common";

import { isMetricSplitConditionIsMet } from "./split-chart-data-view-plugin";

export const DEFAULT_CHART_TYPE = "line";
export const CHANGE_CHART_DATA_PLUGIN_ID = "chartDataManager";
export type ChartDataManagerPluginOptions = {
  data: ChartDatasets;
  labels: Date[];
  activeTab: TabId;
  comparisonDatasets: (ChartDatasets | null)[];
  selectedCompareMetrics: SalesPageMetric[];
  selectedMetrics: SalesPageMetric[];
  benchmarkMainData: number | null;
  benchmarkCompare1Data: number | null;
  benchmarkCompare2Data: number | null;
  chartConfig: ChartConfig;
  getCompareQueryMetadata: GetCompareQueryMetadata;
  pageConfiguration: PageConfiguration & {
    timezone: string;
    dateRangeInterval: DateInterval;
    compareDateRangeInterval: DateInterval | null;
  };
  annotationDataset: AnnotationDataset | null;
  designElementManager: DesignElementManager;
  compareDateRangeLabels?: Date[];
};

export const chartDataManagerPlugin: Plugin = {
  id: CHANGE_CHART_DATA_PLUGIN_ID,
  beforeUpdate(
    chart: Chart<any>,
    _args: any,
    options: ChartDataManagerPluginOptions,
  ) {
    if (!isMetricSplitConditionIsMet(chart)) {
      chart.config.data = buildAggregateMetricDataset(
        options,
        chart.config.type,
      );
    }
  },
};

export const getChartDataManagerOptions = (
  chart: Chart<any>,
): ChartDataManagerPluginOptions =>
  chart.config.options.plugins[CHANGE_CHART_DATA_PLUGIN_ID];

export const getCurrentChartView = (chart: Chart<any>) =>
  getChartDataManagerOptions(chart).chartConfig.chartView;

export const isChartAggregateView = (chart: Chart<any>) =>
  getCurrentChartView(chart) === "aggregate";

export const isChartSplitView = (chart: Chart<any>) =>
  getCurrentChartView(chart) === "split";

export type CustomChartDataset = {
  data: number[];
  yAxisID: Y_AXIS_ID;
  baseBorderColor: string;
  baseBackgroundColor: string | CanvasGradient | CanvasPattern;
  borderColor: string | string[];
  backgroundColor:
    | string
    | CanvasGradient
    | CanvasPattern
    | (string | CanvasGradient | CanvasPattern)[];
  segment: {
    borderColor: (ctx: any) => string;
  };
  dimensions?: Record<string, string>;
  borderDash: number[];
  getIcon?: (options: { borderColor?: string; size?: string }) => string;
  name?: string;
  createBackgroundColor?: (color: string) => string | CanvasPattern;
  compare?: Compare;
  realID: string;
};

const getEndPeriod = (date: Date, timeGranularity: TimeGranularity) => {
  const period = (
    {
      hourly: "hour",
      daily: "day",
      weekly: "week",
      monthly: "month",
    } as const
  )[timeGranularity];

  return moment(date).endOf(period);
};

export const isIncomplete = ({
  date,
  dateRange,
  timeGranularity,
  index,
  total,
}: {
  date: Date;
  dateRange: DateInterval | null;
  timeGranularity: TimeGranularity;
  index: number;
  total: number;
}) => {
  if (!dateRange) {
    return false;
  }

  if (timeGranularity === "hourly") {
    return false;
  }

  if (
    index === 0 &&
    moment(date).format("YYYY-MM-DD") !== dateRange.startDate
  ) {
    return true;
  }

  const endPeriod = getEndPeriod(date, timeGranularity);

  if (
    index === total - 1 &&
    endPeriod.format("YYYY-MM-DD") !== dateRange.endDate
  ) {
    return true;
  }

  const today = moment().startOf("day");

  if (moment(endPeriod).isSameOrAfter(today)) {
    return true;
  }

  return false;
};

const buildAggregateMetricDataset = (
  {
    data,
    comparisonDatasets,
    selectedCompareMetrics,
    selectedMetrics,
    benchmarkMainData,
    benchmarkCompare1Data,
    benchmarkCompare2Data,
    chartConfig: { comparisons },
    annotationDataset,
    pageConfiguration: {
      compareDateRange,
      timeGranularity,
      timezone,
      dateRangeInterval,
      compareDateRangeInterval,
    },
    labels,
    compareDateRangeLabels,
  }: ChartDataManagerPluginOptions,
  chartType: "line" | "bar",
) => {
  const isLineChart = chartType === "line";
  const [mainMetric, compare1, compare2] = selectedMetrics;

  // create an aggregate from the dataset
  const aggregateDataset: metrics.MetricSeries = {};
  labels.forEach((label) => {
    const metricPoint = metrics.newMetricPoint();
    aggregateDataset[label.toISOString()] = metricPoint;
  });
  data.datasets.forEach((dataset) => {
    Object.entries(dataset.series).forEach(([dateLabel, row]) => {
      metrics.addToMetricSeries(aggregateDataset, dateLabel, row);
    });
  });

  const baseColor = "#7081FF";
  const transparentColor = "#7081FF50";

  const lineBorderColor = baseColor;
  const lineBackgroundColor = baseColor;

  const isIncompleteMain = (date: Date, index: number) =>
    isIncomplete({
      date,
      dateRange: dateRangeInterval,
      timeGranularity,
      index,
      total: labels.length,
    });

  const barBorderColor = labels.map((date, index) =>
    isIncompleteMain(date, index) ? transparentColor : baseColor,
  );
  const barBackgroundColor = barBorderColor;

  const chartData: ChartData<any> = {
    labels,
    datasets: [
      buildDataset({
        data: labels.map((date) =>
          calculateMetricValue(
            mainMetric,
            aggregateDataset[date.toISOString()],
          ),
        ),
        yAxisID: MAIN_METRIC_ID,
        realID: MAIN_METRIC_ID,
        baseBorderColor: baseColor,
        baseBackgroundColor: baseColor,
        borderColor: isLineChart ? lineBorderColor : barBorderColor,
        backgroundColor: isLineChart ? lineBackgroundColor : barBackgroundColor,
        borderDash: [],
        segment: {
          borderColor: (ctx: any) =>
            isIncompleteMain(labels[ctx.p0DataIndex], ctx.p0DataIndex) ||
            isIncompleteMain(labels[ctx.p1DataIndex], ctx.p1DataIndex)
              ? transparentColor
              : baseColor,
        },
      }),
    ],
  };

  if (benchmarkMainData !== null && benchmarkMainData !== undefined) {
    chartData.datasets.push({
      ...buildDataset({
        data: labels.map(() => benchmarkMainData),
        yAxisID: MAIN_METRIC_BENCHMARK_ID,
        realID: MAIN_METRIC_BENCHMARK_ID,
        baseBorderColor: baseColor,
        baseBackgroundColor: baseColor,
        borderColor: lineBorderColor,
        backgroundColor: lineBackgroundColor,
        borderDash: [2, 6],
        segment: {
          borderColor: (ctx: any) => baseColor,
        },
      }),
      type: "line",
    });
  }

  comparisons.forEach((compare, index) => {
    const aggregateDatasetToUse: metrics.MetricSeries = {};
    labels.forEach((label) => {
      const metricPoint = metrics.newMetricPoint();
      aggregateDatasetToUse[label.toISOString()] = metricPoint;
    });
    const comparisonDataset = comparisonDatasets[index]?.datasets;

    comparisonDataset?.forEach((dataset) => {
      Object.entries(dataset.series).forEach(([dateLabel, row]) => {
        metrics.addToMetricSeries(aggregateDatasetToUse, dateLabel, row);
      });
    });

    if (index === 0) {
      const metricToUse = compare1 || selectedCompareMetrics?.[0];
      const usingInferedMetric = selectedCompareMetrics?.[0] === metricToUse;

      const baseColor = "#7081FF";
      const transparentColor = "#7081FF50";

      const baseBorderColor = baseColor;
      const baseBackgroundColor = createCompare1BarChartPattern(baseColor);

      const lineBorderColor = baseColor;
      const lineBackgroundColor = baseColor;

      const compareLabels =
        compare.queryKey === "compareDateRange"
          ? compareDateRangeLabels || []
          : labels;

      const isIncompleteCompare1 = (date: Date, index: number) =>
        isIncomplete({
          date,
          dateRange:
            compare.queryKey === "compareDateRange"
              ? compareDateRangeInterval
              : dateRangeInterval,
          timeGranularity,
          index,
          total: compareLabels.length,
        });

      const barBorderColor = compareLabels.map((date, index) =>
        isIncompleteCompare1(date, index) ? transparentColor : baseColor,
      );
      const barBackgroundColor = compareLabels.map((date, index) =>
        isIncompleteCompare1(date, index)
          ? createCompare1BarChartPattern(transparentColor)
          : createCompare1BarChartPattern(baseColor),
      );

      const compare1Dataset = buildDataset({
        data:
          compare.queryKey === "compareDateRange"
            ? buildCompareDateRangeData({
                labels,
                metricSeries: aggregateDatasetToUse,
                metric: mainMetric,
                compareDateRange: compareDateRange as DateInterval,
                timeGranularity,
                timezone,
              })
            : labels.map((date) =>
                calculateMetricValue(
                  metricToUse,
                  aggregateDatasetToUse[date.toISOString()],
                ),
              ),
        yAxisID: usingInferedMetric ? MAIN_METRIC_ID : COMPARE_METRIC_1_ID,
        realID: COMPARE_METRIC_1_ID,
        baseBorderColor,
        baseBackgroundColor,
        borderColor: isLineChart ? lineBorderColor : barBorderColor,
        backgroundColor: isLineChart ? lineBackgroundColor : barBackgroundColor,
        borderDash: COMPARE_METRIC_1_LINE_DASH,
        segment: {
          borderColor: (ctx: any) =>
            isIncompleteCompare1(
              compareLabels[ctx.p0DataIndex],
              ctx.p0DataIndex,
            ) ||
            isIncompleteCompare1(
              compareLabels[ctx.p1DataIndex],
              ctx.p1DataIndex,
            )
              ? transparentColor
              : baseColor,
        },
        compare,
      });
      chartData.datasets.push(compare1Dataset);

      if (
        benchmarkCompare1Data !== null &&
        benchmarkCompare1Data !== undefined
      ) {
        chartData.datasets.push({
          ...buildDataset({
            data: labels.map(() => benchmarkCompare1Data),
            yAxisID: usingInferedMetric
              ? MAIN_METRIC_ID
              : COMPARE_METRIC_1_BENCHMARK_ID,
            realID: COMPARE_METRIC_1_BENCHMARK_ID,
            baseBorderColor: baseColor,
            baseBackgroundColor: baseColor,
            borderColor: lineBorderColor,
            backgroundColor: lineBackgroundColor,
            borderDash: [2, 6],
            segment: {
              borderColor: (ctx: any) => baseColor,
            },
          }),
          type: "line",
        });
      }
    } else if (index === 1) {
      const metricToUse = compare2 || selectedCompareMetrics?.[1];
      const usingInferedMetric = selectedCompareMetrics?.[1] === metricToUse;

      const baseColor = "#7081FF";
      const transparentColor = "#7081FF50";

      const baseBorderColor = baseColor;
      const baseBackgroundColor = createCompare2BarChartPattern(baseColor);

      const lineBorderColor = baseColor;
      const lineBackgroundColor = baseColor;

      const compareLabels =
        compare.queryKey === "compareDateRange"
          ? compareDateRangeLabels || []
          : labels;

      const isIncompleteCompare2 = (date: Date, index: number) =>
        isIncomplete({
          date,
          dateRange:
            compare.queryKey === "compareDateRange"
              ? compareDateRangeInterval
              : dateRangeInterval,
          timeGranularity,
          index,
          total: compareLabels.length,
        });

      const barBorderColor = compareLabels.map((date, index) =>
        isIncompleteCompare2(date, index) ? transparentColor : baseColor,
      );
      const barBackgroundColor = compareLabels.map((date, index) =>
        isIncompleteCompare2(date, index)
          ? createCompare2BarChartPattern(transparentColor)
          : createCompare2BarChartPattern(baseColor),
      );

      const compare2Dataset = buildDataset({
        data:
          compare.queryKey === "compareDateRange"
            ? buildCompareDateRangeData({
                labels,
                metricSeries: aggregateDatasetToUse,
                metric: mainMetric,
                compareDateRange: compareDateRange as DateInterval,
                timeGranularity,
                timezone,
              })
            : labels.map((date) =>
                calculateMetricValue(
                  metricToUse,
                  aggregateDatasetToUse[date.toISOString()],
                ),
              ),
        yAxisID: usingInferedMetric ? MAIN_METRIC_ID : COMPARE_METRIC_2_ID,
        realID: COMPARE_METRIC_2_ID,
        baseBorderColor,
        baseBackgroundColor,
        borderColor: isLineChart ? lineBorderColor : barBorderColor,
        backgroundColor: isLineChart ? lineBackgroundColor : barBackgroundColor,
        borderDash: COMPARE_METRIC_2_LINE_DASH,
        segment: {
          borderColor: (ctx: any) =>
            isIncompleteCompare2(
              compareLabels[ctx.p0DataIndex],
              ctx.p0DataIndex,
            ) ||
            isIncompleteCompare2(
              compareLabels[ctx.p1DataIndex],
              ctx.p1DataIndex,
            )
              ? transparentColor
              : baseColor,
        },
        compare,
      });
      chartData.datasets.push(compare2Dataset);

      if (
        benchmarkCompare2Data !== null &&
        benchmarkCompare2Data !== undefined
      ) {
        chartData.datasets.push({
          ...buildDataset({
            data: labels.map(() => benchmarkCompare2Data),
            yAxisID: usingInferedMetric
              ? MAIN_METRIC_ID
              : COMPARE_METRIC_2_BENCHMARK_ID,
            realID: COMPARE_METRIC_2_BENCHMARK_ID,
            baseBorderColor: baseColor,
            baseBackgroundColor: baseColor,
            borderColor: lineBorderColor,
            backgroundColor: lineBackgroundColor,
            borderDash: [2, 6],
            segment: {
              borderColor: (ctx: any) => baseColor,
            },
          }),
          type: "line",
        });
      }
    }
  });

  annotationDataset && chartData.datasets.push(annotationDataset);

  return chartData;
};

const setChartType = (chart: Chart<any>, { chartType }: ChartConfig) => {
  chart.config.type = chartType;
  if (chartType === "line") {
    chart.config.options.scales.x.offset = false;
  } else if (chartType === "bar") {
    chart.config.options.scales.x.offset = true;
  }
};

const setChartView = (chart: Chart<any>, chartConfig: ChartConfig) => {
  if (
    chartConfig.chartView === "split" &&
    chartConfig.comparisons.length <= 1
  ) {
    set(
      chart,
      "config.options.plugins.chartDataManager.chartConfig.chartView",
      "split",
    );
  } else {
    set(
      chart,
      "config.options.plugins.chartDataManager.chartConfig.chartView",
      "aggregate",
    );
  }
};

export const setChartDataManagerOptions = (
  chart: ChartJSOrUndefined | null,
  chartDataManager: ChartDataManagerPluginOptions,
) => {
  if (!chart) {
    return;
  }
  set(chart, "config.options.plugins.chartDataManager", chartDataManager);
  setChartView(chart, chartDataManager.chartConfig);
  setChartType(chart, chartDataManager.chartConfig);

  chart.update("none");
};
